import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import Axios, { authHeaders } from "../../utils/axios";

// GIS trial policy
export const fetchGisTrialConfig = createAsyncThunk(
    "gisTrial/fetchConfig",
    async (_, { rejectWithValue }) => {
        try {
            const res = await Axios.get("/gis-trial/config", authHeaders());
            return res.data;
        } catch (err) {
            return rejectWithValue(err.response?.data || err.message);
        }
    }
);

export const updateGisTrialConfig = createAsyncThunk(
    "gisTrial/updateConfig",
    async (data, { rejectWithValue }) => {
        try {
            const res = await Axios.patch("/gis-trial/config", data, authHeaders());
            return res.data;
        } catch (err) {
            return rejectWithValue(err.response?.data || err.message);
        }
    }
);

export const fetchGisTrialStats = createAsyncThunk(
    "gisTrial/fetchStats",
    async (_, { rejectWithValue }) => {
        try {
            const res = await Axios.get("/gis-trial/stats", authHeaders());
            return res.data;
        } catch (err) {
            return rejectWithValue(err.response?.data || err.message);
        }
    }
);

export const fetchGisTrials = createAsyncThunk(
    "gisTrial/fetchTrials",
    async ({ search = "", status = "", page = 1, limit = 10 }, { rejectWithValue }) => {
        try {
            const res = await Axios.get(
                `/gis-trial/trials?search=${search}&status=${status}&page=${page}&limit=${limit}`,
                authHeaders()
            );
            return res.data;
        } catch (err) {
            return rejectWithValue(err.response?.data || err.message);
        }
    }
);

// Entitlements
export const fetchUserEntitlements = createAsyncThunk(
    "gisTrial/fetchUserEntitlements",
    async (userId, { rejectWithValue }) => {
        try {
            const res = await Axios.get(`/entitlements/users/${userId}`, authHeaders());
            return res.data;
        } catch (err) {
            return rejectWithValue(err.response?.data || err.message);
        }
    }
);

export const grantEntitlement = createAsyncThunk(
    "gisTrial/grantEntitlement",
    async ({ userId, data }, { rejectWithValue }) => {
        try {
            const res = await Axios.post(`/entitlements/users/${userId}`, data, authHeaders());
            return res.data;
        } catch (err) {
            return rejectWithValue(err.response?.data || err.message);
        }
    }
);

export const revokeEntitlement = createAsyncThunk(
    "gisTrial/revokeEntitlement",
    async (id, { rejectWithValue }) => {
        try {
            const res = await Axios.delete(`/entitlements/${id}`, authHeaders());
            return res.data;
        } catch (err) {
            return rejectWithValue(err.response?.data || err.message);
        }
    }
);

const gisTrialSlice = createSlice({
    name: "gisTrial",
    initialState: {
        config: null,
        stats: null,
        trials: [],
        total: 0,
        entitlements: [],
        isLoading: false,
        isSaving: false,
        error: null,
    },
    reducers: {
        clearGisTrialError: (state) => {
            state.error = null;
        },
    },
    extraReducers: (builder) => {
        builder
            // Config
            .addCase(fetchGisTrialConfig.pending, (state) => {
                state.isLoading = true;
                state.error = null;
            })
            .addCase(fetchGisTrialConfig.fulfilled, (state, action) => {
                state.isLoading = false;
                state.config = action.payload?.data || null;
            })
            .addCase(fetchGisTrialConfig.rejected, (state, action) => {
                state.isLoading = false;
                state.error = action.payload;
            })

            .addCase(updateGisTrialConfig.pending, (state) => {
                state.isSaving = true;
                state.error = null;
            })
            .addCase(updateGisTrialConfig.fulfilled, (state, action) => {
                state.isSaving = false;
                state.config = action.payload?.data || state.config;
            })
            .addCase(updateGisTrialConfig.rejected, (state, action) => {
                state.isSaving = false;
                state.error = action.payload;
            })

            // Stats
            .addCase(fetchGisTrialStats.fulfilled, (state, action) => {
                state.stats = action.payload?.data || null;
            })

            // Trials
            .addCase(fetchGisTrials.pending, (state) => {
                state.isLoading = true;
                state.error = null;
            })
            .addCase(fetchGisTrials.fulfilled, (state, action) => {
                state.isLoading = false;
                state.trials = action.payload?.data || [];
                state.total = action.payload?.meta?.total || 0;
            })
            .addCase(fetchGisTrials.rejected, (state, action) => {
                state.isLoading = false;
                state.error = action.payload;
            })

            // Entitlements
            .addCase(fetchUserEntitlements.pending, (state) => {
                state.isLoading = true;
            })
            .addCase(fetchUserEntitlements.fulfilled, (state, action) => {
                state.isLoading = false;
                state.entitlements = action.payload?.data || [];
            })
            .addCase(fetchUserEntitlements.rejected, (state, action) => {
                state.isLoading = false;
                state.error = action.payload;
            });
    },
});

export const { clearGisTrialError } = gisTrialSlice.actions;
export default gisTrialSlice.reducer;
